import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check, Menu, X, QrCode, MessageSquare, Users, BarChart3, ShieldCheck, Zap, Building2, Radio, GraduationCap, Landmark } from "lucide-react";

/* =============== Shell =============== */
function Header() {
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/features", label: "Features" },
    { to: "/solutions", label: "Solutions" },
    { to: "/pricing", label: "Pricing" },
    { to: "/contact", label: "Contact" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="shrink-0"><Logo className="h-8" /></Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="text-sm text-gray-600 hover:text-gray-900 transition-colors" data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900" data-testid="nav-login">Login</Link>
          <Link to="/book-demo" className="text-sm bg-gray-900 text-white rounded-md px-4 py-2 hover:bg-gray-800 transition-colors" data-testid="nav-book-demo">
            Book a Demo
          </Link>
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-gray-700" onClick={() => setOpen(false)}>{l.label}</Link>
            ))}
            <Link to="/login" className="text-sm text-gray-700" onClick={() => setOpen(false)}>Login</Link>
            <Link to="/book-demo" className="text-sm bg-gray-900 text-white rounded-md px-4 py-2 text-center" onClick={() => setOpen(false)}>
              Book a Demo
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2">
          <Logo className="h-8 mb-4" />
          <p className="text-sm text-gray-500 max-w-sm">
            Turn every exhibition visitor into a conversation. The AI engagement platform built for enterprise exhibitors.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-gray-500 font-semibold">Product</div>
          <div className="mt-4 space-y-2 text-sm text-gray-700">
            <div><Link to="/features" className="hover:text-gray-900">Features</Link></div>
            <div><Link to="/solutions" className="hover:text-gray-900">Solutions</Link></div>
            <div><Link to="/pricing" className="hover:text-gray-900">Pricing</Link></div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-gray-500 font-semibold">Company</div>
          <div className="mt-4 space-y-2 text-sm text-gray-700">
            <div><Link to="/contact" className="hover:text-gray-900">Contact</Link></div>
            <div><Link to="/book-demo" className="hover:text-gray-900">Book a Demo</Link></div>
            <div><Link to="/login" className="hover:text-gray-900">Login</Link></div>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-gray-400 flex justify-between">
          <div>© {new Date().getFullYear()} ExpoFlo AI</div>
          <div>Ask · Discover · Connect</div>
        </div>
      </div>
    </footer>
  );
}

function MarketingShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

/* =============== Sections =============== */
function Eyebrow({ children }) {
  return <div className="text-xs uppercase tracking-[0.22em] text-purple-700 font-bold">{children}</div>;
}

function SectionHeader({ eyebrow, title, subtitle, align = "left" }) {
  return (
    <div className={align === "center" ? "text-center max-w-2xl mx-auto" : "max-w-3xl"}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-gray-900 mt-4">{title}</h2>
      {subtitle && <p className="text-base text-gray-600 mt-4 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

/* =============== Home =============== */
export function HomePage() {
  return (
    <MarketingShell>
      {/* Hero */}
      <section className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>Ask · Discover · Connect</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-gray-900 mt-5 leading-[1.05]">
              Turn every exhibition visitor into a{" "}
              <span className="brand-gradient-text">conversation</span>.
            </h1>
            <p className="text-lg text-gray-600 mt-6 max-w-2xl leading-relaxed">
              Engage visitors with AI-powered voice assistants, capture leads automatically and showcase your products using intelligent conversations — no downloads, no logins.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book-demo" className="bg-gray-900 text-white rounded-md px-5 py-3 text-sm font-medium hover:bg-gray-800 transition-colors inline-flex items-center gap-2" data-testid="hero-book-demo">
                Book a Demo <ArrowRight size={16} />
              </Link>
              <Link to="/features" className="border border-gray-200 rounded-md px-5 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors" data-testid="hero-see-how">
                See How It Works
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.16em] text-gray-400">
              <span>Enterprise-grade</span>
              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
              <span>Multi-tenant</span>
              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
              <span>GDPR-ready</span>
            </div>
          </div>

          {/* Right — device mock */}
          <div className="lg:col-span-5 hidden sm:block">
            <div className="visitor-bg rounded-[2rem] p-6 aspect-[3/4] flex flex-col text-white overflow-hidden relative border border-gray-100">
              <div className="flex items-center justify-between">
                <Logo className="h-6" variant="light" />
                <div className="text-[10px] uppercase tracking-widest text-white/50">Live</div>
              </div>
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Powered by ExpoFlo AI</div>
                <div className="font-display text-lg font-bold mt-1">Capital Park Technologies</div>
              </div>
              <div className="mt-auto space-y-3">
                <div className="bg-black/40 border border-white/10 rounded-2xl p-4">
                  <div className="text-xs font-semibold flex gap-1">Hi there! 👋</div>
                  <div className="text-xs text-white/70 mt-1">I'm ExpoFlo AI, your virtual assistant. Ask me anything.</div>
                </div>
                <button className="w-full brand-gradient rounded-full py-3 text-sm font-medium flex items-center justify-center gap-2">
                  <Radio size={14} /> Tap to speak
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow="How it works" title="From QR scan to qualified lead — in seconds." />
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              { n: "01", icon: <QrCode size={20} />, title: "Visitor scans a QR code", body: "No app. No login. The visitor lands on your branded assistant instantly." },
              { n: "02", icon: <MessageSquare size={20} />, title: "AI answers using your knowledge", body: "Upload PDFs, brochures and FAQs. The assistant answers in your voice." },
              { n: "03", icon: <Users size={20} />, title: "Capture leads automatically", body: "Book demos, download brochures, talk to sales — everything ends in your CRM." },
            ].map((s) => (
              <div key={s.n} className="border border-gray-200 rounded-xl p-6 hover:border-gray-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-gray-900 text-white flex items-center justify-center">{s.icon}</div>
                  <div className="text-xs font-mono text-gray-300">{s.n}</div>
                </div>
                <div className="font-display text-lg font-semibold mt-6 text-gray-900">{s.title}</div>
                <div className="text-sm text-gray-600 mt-2 leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 border-b border-gray-100 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow="Features" title="Built for exhibitors that take engagement seriously." />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {[
              { icon: <MessageSquare size={18} />, title: "Voice + text assistant", body: "Visitors tap to speak or type. AI answers using your uploaded knowledge base." },
              { icon: <QrCode size={18} />, title: "Event QR codes", body: "Every event gets a branded QR code. Print-ready PNG and SVG downloads." },
              { icon: <Users size={18} />, title: "Automatic lead capture", body: "Book Demo, Download Brochure, Talk to Sales, Request Pricing — leads flow in." },
              { icon: <BarChart3 size={18} />, title: "Live analytics", body: "See scans, conversations, popular questions and conversion in real time." },
              { icon: <ShieldCheck size={18} />, title: "Multi-tenant isolation", body: "Every organization's data is fully isolated. Enterprise security by default." },
              { icon: <Zap size={18} />, title: "Provider-agnostic AI", body: "OpenAI today. Anthropic, Gemini and Azure ready. Your API keys, your control." },
            ].map((f) => (
              <div key={f.title} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="w-9 h-9 rounded-md bg-purple-50 text-purple-700 flex items-center justify-center">{f.icon}</div>
                <div className="font-display text-lg font-semibold mt-5 text-gray-900">{f.title}</div>
                <div className="text-sm text-gray-600 mt-2 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions preview */}
      <section className="py-24 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Solutions" title="One platform. Every enterprise vertical." subtitle="Deployed by banks, telcos, mining companies, universities, government departments and exhibition organizers." />
            <Link to="/solutions" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900">
              Explore solutions <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {[
              { icon: <Landmark size={16} />, name: "Banks" },
              { icon: <Radio size={16} />, name: "Telecommunications" },
              { icon: <Building2 size={16} />, name: "Mining" },
              { icon: <ShieldCheck size={16} />, name: "Government" },
              { icon: <GraduationCap size={16} />, name: "Universities" },
              { icon: <Users size={16} />, name: "Exhibition Organizers" },
            ].map((v) => (
              <div key={v.name} className="border border-gray-200 rounded-lg px-4 py-4 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                <span className="text-purple-700">{v.icon}</span>
                <span className="text-sm text-gray-800">{v.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="py-24 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow="Pricing" title="Enterprise pricing. Managed onboarding." subtitle="No self-signup. Every organization is onboarded manually by the ExpoFlo team so you get a tailored, secure deployment." align="center" />
          <PricingCards />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 border-b border-gray-100 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeader eyebrow="FAQ" title="Answers before you ask." />
          <div className="mt-10 divide-y divide-gray-200 border-y border-gray-200">
            {[
              { q: "Do visitors need to install an app?", a: "No. Visitors simply scan a QR code and land on your branded AI assistant. No download, no login." },
              { q: "Where is our knowledge stored?", a: "ExpoFlo AI stores your knowledge base securely. We do not use OpenAI-hosted knowledge bases. Every organization's data is fully isolated." },
              { q: "Can we use our own OpenAI or Anthropic key?", a: "Yes. The AI service layer is provider-agnostic. You can bring your own API keys for OpenAI, Anthropic, Gemini or Azure OpenAI." },
              { q: "How do we get started?", a: "Book a demo. Once approved, the ExpoFlo team provisions your organization, admin account and knowledge base." },
            ].map((f) => (
              <details key={f.q} className="py-5 group">
                <summary className="cursor-pointer flex justify-between items-center list-none">
                  <span className="text-base font-medium text-gray-900">{f.q}</span>
                  <span className="text-gray-400 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-gray-600 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionHeader eyebrow="Get started" title="Ready to turn scans into conversations?" subtitle="Book a 20-minute demo with our team. We'll walk you through the platform and set up a sandbox for your next event." align="center" />
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/book-demo" className="bg-gray-900 text-white rounded-md px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors inline-flex items-center gap-2" data-testid="cta-book-demo">
              Book a Demo <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="border border-gray-200 rounded-md px-6 py-3 text-sm font-medium hover:bg-gray-50 transition-colors">Contact sales</Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

/* =============== Features Page =============== */
export function FeaturesPage() {
  return (
    <MarketingShell>
      <section className="py-24 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow="Features" title="Every tool your exhibition team needs." subtitle="Voice-first AI, event QR codes, knowledge base management, live analytics and multi-tenant isolation — in one platform." />
          <div className="grid md:grid-cols-2 gap-10 mt-16">
            {[
              { title: "AI Voice Assistant", body: "Visitors tap to speak. We transcribe with Whisper, answer with GPT and stream results back — all under two seconds." },
              { title: "Knowledge Base", body: "Upload PDFs, DOCX, TXT, brochures, product sheets and website URLs. The assistant answers using ONLY your content." },
              { title: "Event QR Codes", body: "Every event gets a branded QR code. Downloadable in PNG and SVG. Always points to your live public event URL." },
              { title: "Lead Capture", body: "Book Demo, Download Brochure, Talk to Sales, Request Pricing. Every action creates a scoped lead in your organization." },
              { title: "Analytics", body: "Track scans, conversations, popular questions, most requested products and lead conversion — all in real time." },
              { title: "Provider-agnostic AI", body: "OpenAI GPT-5.2 by default. Swap to Anthropic Claude, Gemini or Azure OpenAI without changing the platform." },
              { title: "Branding per Organization", body: "Every org gets its own logo, brand color, welcome message, assistant name and suggested questions." },
              { title: "Multi-tenant Security", body: "Server-side authorization. Data isolation by design. Organization admins never see another organization's data." },
            ].map((f) => (
              <div key={f.title}>
                <div className="w-8 h-8 rounded-md brand-gradient" />
                <div className="font-display text-xl font-semibold mt-5 text-gray-900">{f.title}</div>
                <div className="text-sm text-gray-600 mt-2 leading-relaxed max-w-md">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

/* =============== Solutions Page =============== */
export function SolutionsPage() {
  const items = [
    { icon: <Landmark size={20} />, name: "Banks & Financial Services", body: "Onboard new customers at branch expos. Answer product questions and capture applications automatically." },
    { icon: <Radio size={20} />, name: "Telecommunications", body: "Explain plans and devices at retail activations. Book installation and sales appointments on the spot." },
    { icon: <Building2 size={20} />, name: "Mining & Resources", body: "Showcase operations, community programmes and career opportunities at industry expos." },
    { icon: <ShieldCheck size={20} />, name: "Government Departments", body: "Deliver citizen services and information at public roadshows. Capture applications and enquiries." },
    { icon: <GraduationCap size={20} />, name: "Universities", body: "Explain programmes, admissions and campus life at open days. Capture prospective student leads." },
    { icon: <Users size={20} />, name: "Exhibition Organizers", body: "Provide a branded AI concierge across an entire event. Direct visitors to booths and services." },
  ];
  return (
    <MarketingShell>
      <section className="py-24 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow="Solutions" title="Built for every enterprise vertical." subtitle="ExpoFlo AI powers exhibition engagement for banks, telcos, mining, government, universities and exhibition organizers." />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            {items.map((i) => (
              <div key={i.name} className="border border-gray-200 rounded-xl p-6 hover:border-gray-300 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center">{i.icon}</div>
                <div className="font-display text-lg font-semibold mt-5 text-gray-900">{i.name}</div>
                <div className="text-sm text-gray-600 mt-2 leading-relaxed">{i.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

/* =============== Pricing Page =============== */
function PricingCards() {
  const tiers = [
    {
      name: "Starter",
      price: "On request",
      description: "One organization, up to 3 events per year.",
      features: ["1 organization workspace", "Up to 3 events", "5 GB knowledge base storage", "Standard AI (GPT-5.2)", "Email support"],
      cta: "Book a demo",
    },
    {
      name: "Growth",
      price: "On request",
      description: "Growing brands with multiple events per quarter.",
      features: ["1 organization workspace", "Unlimited events", "25 GB knowledge base storage", "Voice + text assistant", "Priority support"],
      cta: "Book a demo",
      featured: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      description: "Multiple divisions, custom AI provider, dedicated success manager.",
      features: ["Unlimited organizations", "Custom AI providers (Anthropic, Azure, Gemini)", "SSO & custom SLA", "Dedicated onboarding", "24/7 support"],
      cta: "Contact sales",
    },
  ];
  return (
    <div className="grid md:grid-cols-3 gap-6 mt-12">
      {tiers.map((t) => (
        <div key={t.name} className={`rounded-xl p-8 border ${t.featured ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white"}`}>
          <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-70">{t.name}</div>
          <div className="font-display text-3xl font-semibold tracking-tight mt-3">{t.price}</div>
          <div className={`text-sm mt-2 ${t.featured ? "text-gray-300" : "text-gray-600"}`}>{t.description}</div>
          <ul className="mt-6 space-y-2.5">
            {t.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check size={16} className={t.featured ? "text-pink-400 mt-0.5 shrink-0" : "text-purple-700 mt-0.5 shrink-0"} />
                <span className={t.featured ? "text-gray-100" : "text-gray-700"}>{f}</span>
              </li>
            ))}
          </ul>
          <Link to="/book-demo" className={`mt-8 block text-center rounded-md py-2.5 text-sm font-medium ${t.featured ? "bg-white text-gray-900 hover:bg-gray-100" : "bg-gray-900 text-white hover:bg-gray-800"} transition-colors`}>
            {t.cta}
          </Link>
        </div>
      ))}
    </div>
  );
}

export function PricingPage() {
  return (
    <MarketingShell>
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow="Pricing" title="Enterprise pricing. Managed onboarding." subtitle="No self-signup. Every organization is onboarded manually by the ExpoFlo team." align="center" />
          <PricingCards />
          <p className="text-xs text-gray-400 text-center mt-12">Billing is handled offline. Subscriptions are managed manually.</p>
        </div>
      </section>
    </MarketingShell>
  );
}

/* =============== Contact & Book Demo =============== */
export function ContactPage() {
  return (
    <MarketingShell>
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-16">
          <div>
            <SectionHeader eyebrow="Contact" title="Talk to our team." subtitle="Enterprise sales, partnerships and support enquiries." />
          </div>
          <div className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Email</div>
              <a href="mailto:events@expoflo.online" className="text-sm text-gray-900 hover:text-purple-700 mt-2 inline-block" data-testid="contact-email">events@expoflo.online</a>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Phone</div>
              <a href="tel:+26771835283" className="text-sm text-gray-900 hover:text-purple-700 mt-2 inline-block" data-testid="contact-phone">+267 71 835 283</a>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Book a demo</div>
              <Link to="/book-demo" className="text-sm text-purple-700 hover:text-purple-900 mt-2 inline-flex items-center gap-1">Schedule now <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function BookDemoPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/demo-request", form);
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MarketingShell>
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-16">
          <div>
            <SectionHeader eyebrow="Book a demo" title="See ExpoFlo AI in 20 minutes." subtitle="We'll walk you through a live sandbox and set up a demo organization for your next event." />
            <ul className="mt-8 space-y-3">
              {["Live product walkthrough", "Custom sandbox for your team", "Pricing & onboarding plan"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-gray-700">
                  <Check size={16} className="text-purple-700" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div>
            {done ? (
              <div className="border border-gray-200 rounded-xl p-8 text-center">
                <div className="w-12 h-12 rounded-full brand-gradient mx-auto flex items-center justify-center text-white mb-4"><Check size={22} /></div>
                <div className="font-display text-xl font-semibold text-gray-900">Request received</div>
                <p className="text-sm text-gray-600 mt-2">Our team will reach out within one business day.</p>
                <button onClick={() => nav("/")} className="mt-6 text-sm text-purple-700 hover:text-purple-900" data-testid="demo-back-home">Back to home →</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="book-demo-form">
                {[
                  { k: "full_name", label: "Full name", required: true },
                  { k: "company", label: "Company", required: true },
                  { k: "email", label: "Work email", type: "email", required: true },
                  { k: "phone", label: "Phone (optional)", type: "tel" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">{f.label}</label>
                    <input
                      type={f.type || "text"}
                      required={f.required}
                      value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      className="mt-2 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      data-testid={`demo-${f.k}`}
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">What's your use case?</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    data-testid="demo-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gray-900 text-white rounded-md py-3 text-sm font-medium hover:bg-gray-800 disabled:opacity-60 transition-colors"
                  data-testid="demo-submit"
                >
                  {submitting ? "Submitting…" : "Request demo"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
